import { useState } from "react";
import { AvatarEditable } from "@/components/admin/AvatarEditable";
import { ModalSecciones, type SeccionModal } from "@/components/ui/modal-secciones";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonationTarget } from "@/contexts/ImpersonationTargetContext";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Key, Pencil, Shield, SlidersHorizontal, UserCog } from "lucide-react";
import { useActivityLogger } from "@/hooks/useActivityLogger";
import { useAutorizacionCambioComprador } from "@/hooks/useAutorizacionCambioComprador";
import { esCancelacionCambioDatos } from "@/lib/usuarios/cambio-datos-comprador";
import { FORM_MIS_DATOS, SeccionDatos, type DatosEditables } from "./mi-cuenta/SeccionDatos";
import { Button } from "@/components/ui/button";
import { useProyectosConAcceso } from "./mi-cuenta/useProyectosConAcceso";
import { APP_VERSION } from "@/lib/config";
import { SeccionRoles } from "./mi-cuenta/SeccionRoles";
import { SeccionPreferencias } from "./mi-cuenta/SeccionPreferencias";
import { SeccionSeguridad } from "./mi-cuenta/SeccionSeguridad";
import { SeccionAvisos } from "./mi-cuenta/SeccionAvisos";

type Seccion = "datos" | "roles" | "preferencias" | "seguridad" | "avisos";

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserSettingsDialog({ open, onOpenChange }: UserSettingsDialogProps) {
  const { profile: perfilSesion, refreshProfile } = useAuth();
  // "Ver como": mismo modal con la información de la persona vista y en SOLO LECTURA (nada se
  // guarda mientras se ve como otra persona). Sin ella, la cuenta de la sesión, editable.
  const target = useImpersonationTarget();
  const soloLectura = !!target;
  const fotoVista = useFotoPerfilVista();
  const profile = target
    ? {
        ...perfilSesion,
        nombre: (target.nombre ?? "").trim() || target.email,
        email: target.email,
        id_persona: target.personaId ?? null,
        rol_id: target.rolId ?? null,
        rol_nombre: target.rolNombre ?? null,
        foto_perfil_url: fotoVista,
      }
    : perfilSesion;
  const queryClient = useQueryClient();
  const autorizacion = useAutorizacionCambioComprador("/admin/perfil");
  const { registrarActualizacion } = useActivityLogger();
  // Rol Inmobiliaria (rol_id 4): no edita datos personales desde aquí
  const isInmobiliariaRole = profile?.rol_id === 4;
  const seccionInicial: Seccion = isInmobiliariaRole ? "roles" : "datos";
  const [seccion, setSeccion] = useState<Seccion>(seccionInicial);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [isCreatingPersona, setIsCreatingPersona] = useState(false);
  const [localIdPersona, setLocalIdPersona] = useState<number | null>(null);

  const proyectos = useProyectosConAcceso(open, target ? { email: target.email, rolId: target.rolId ?? null } : null);

  // Use localIdPersona if set (after creating persona), otherwise use profile's id_persona
  const effectiveIdPersona = localIdPersona || profile?.id_persona;

  // Query for user's persona data
  const { data: personaData, isLoading: personaLoading, refetch: refetchPersona } = useQuery({
    queryKey: ["user_persona_data", effectiveIdPersona],
    queryFn: async () => {
      if (!effectiveIdPersona) return null;

      const { data, error } = await supabase
        .from('personas')
        .select('*')
        .eq('id', effectiveIdPersona)
        .single();

      if (error) {
        console.error('Error fetching persona:', error);
        return null;
      }

      return data;
    },
    enabled: open && !!effectiveIdPersona,
  });

  // Function to create a new persona record if user doesn't have one
  const handleCreateAndEditProfile = async () => {
    if (effectiveIdPersona) return;

    setIsCreatingPersona(true);
    try {
      // Create a new persona record with basic info
      const { data: newPersona, error: insertError } = await supabase
        .from('personas')
        .insert({
          tipo_persona: 'pf',
          nombre_legal: profile?.nombre || 'Sin nombre',
          email: profile?.email || '',
          activo: true,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Update the user record with the new id_persona
      const { error: updateError } = await supabase
        .from('usuarios')
        .update({ id_persona: newPersona.id })
        .eq('email', profile?.email);

      if (updateError) throw updateError;

      // Set local id_persona and refresh profile
      setLocalIdPersona(newPersona.id);
      await refreshProfile();
      await refetchPersona();
      
      toast.success("Se creó tu ficha. Ahora puedes completar tus datos.");
    } catch (error: any) {
      console.error('Error creating persona:', error);
      toast.error("Error al crear el perfil: " + error.message);
    } finally {
      setIsCreatingPersona(false);
    }
  };

  const handleProfileUpdate = async (data: DatosEditables): Promise<boolean> => {
    if (!effectiveIdPersona) {
      toast.error("No se encontró información de persona asociada");
      return false;
    }

    setIsSavingProfile(true);

    try {
      // Si tu persona también es compradora, nombre, correo y teléfono van a una solicitud de
      // autorización (la puedes resolver tú con justificación) y se guarda el resto.
      const idPersona = effectiveIdPersona;
      const { resultado: { error }, solicitud } = await autorizacion.guardar(
        idPersona,
        {
          nombre_legal: data.nombre_legal,
          telefono: data.telefono,
          clave_pais_telefono: data.clave_pais_telefono,
          curp: data.curp,
          fecha_actualizacion: new Date().toISOString(),
        },
        (p) => supabase.from('personas').update(p).eq('id', idPersona),
      );
      autorizacion.avisar(solicitud);

      if (error) throw error;

      // Update usuario name and sync phone data if changed
      const usuarioUpdateData: Record<string, any> = {};
      
      if (data.nombre_legal) {
        usuarioUpdateData.nombre = data.nombre_legal;
      }
      
      // Sincronizar teléfono con tabla usuarios
      if (data.telefono !== undefined) {
        usuarioUpdateData.telefono = data.telefono;
      }
      if (data.clave_pais_telefono !== undefined) {
        usuarioUpdateData.clave_pais_telefono = data.clave_pais_telefono;
      }
      
      if (Object.keys(usuarioUpdateData).length > 0) {
        usuarioUpdateData.fecha_actualizacion = new Date().toISOString();
        await supabase
          .from('usuarios')
          .update(usuarioUpdateData as any)
          .eq('email', profile.email);
      }

      await registrarActualizacion(
        'datos_personales',
        { id_persona: effectiveIdPersona },
        { id_persona: effectiveIdPersona, campos_actualizados: Object.keys(data) },
        'actualizar_datos_personales_configuracion'
      );
      toast.success("Datos actualizados correctamente");
      queryClient.invalidateQueries({ queryKey: ["user_persona_data"] });
      refreshProfile();
      return true;
    } catch (error: any) {
      if (esCancelacionCambioDatos(error)) return false;
      console.error('Error updating profile:', error);
      await registrarActualizacion(
        'datos_personales',
        { id_persona: effectiveIdPersona },
        { id_persona: effectiveIdPersona },
        'actualizar_datos_personales_configuracion',
        'error',
        error.message
      );
      toast.error("Error al actualizar los datos: " + error.message);
      return false;
    } finally {
      setIsSavingProfile(false);
    }
  };

  const secciones: SeccionModal<Seccion>[] = [
    ...(!isInmobiliariaRole
      ? [{ id: "datos" as const, label: "Mis datos", icon: UserCog, descripcion: "Tu información personal, domicilio y datos fiscales." }]
      : []),
    { id: "roles", label: "Roles y acceso", icon: Shield, descripcion: "Con qué rol entras y a qué proyectos tienes acceso." },
    // Preferencias (tema del dispositivo), seguridad y avisos son de la sesión, no de la persona vista.
    ...(soloLectura
      ? []
      : [
          { id: "preferencias" as const, label: "Preferencias", icon: SlidersHorizontal, descripcion: "Cómo se ve el panel para ti." },
          { id: "seguridad" as const, label: "Seguridad", icon: Key, descripcion: "La contraseña con la que entras." },
          { id: "avisos" as const, label: "Mis avisos", icon: Bell, descripcion: "Los correos del sistema que te llegan y por qué." },
        ]),
  ];

  const nombre = personaData?.nombre_legal || profile?.nombre || "Sin nombre";
  // CTA de "Mis datos" en el encabezado de la sección: Editar, o Cancelar / Guardar mientras se edita
  // (Guardar envía el formulario de la sección por su id).
  const accionSeccion =
    !soloLectura && seccion === "datos" && personaData ? (
      editandoDatos ? (
        <>
          <Button variant="cancel" size="compact" onClick={() => setEditandoDatos(false)} disabled={isSavingProfile}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_MIS_DATOS} variant="primary-outline" size="compact" disabled={isSavingProfile}>
            {isSavingProfile ? "Guardando..." : "Guardar cambios"}
          </Button>
        </>
      ) : (
        <Button variant="primary-outline" size="compact" onClick={() => setEditandoDatos(true)}>
          <Pencil /> Editar
        </Button>
      )
    ) : undefined;

  return (
    <>
      {autorizacion.dialogo}
      {/* Modal de secciones (ui/modal-secciones): tamaño fijo, menú a la izquierda y cada sección
          se edita en su lugar. */}
      <ModalSecciones<Seccion>
        open={open}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            setSeccion(seccionInicial);
            setEditandoDatos(false);
          }
          onOpenChange(newOpen);
        }}
        titulo={soloLectura ? `Cuenta de ${nombre}` : "Mi cuenta"}
        descripcion={soloLectura ? "Datos, roles y acceso de la persona que estás viendo." : "Tus datos, roles, preferencias, seguridad y avisos."}
        secciones={secciones}
        value={seccion}
        onChange={(id) => {
          setSeccion(id);
          setEditandoDatos(false);
        }}
        accion={accionSeccion}
        encabezado={
          <div className="flex min-w-0 items-center gap-3 sm:flex-col sm:gap-2 sm:text-center">
            <AvatarEditable nombre={nombre} fotoUrl={profile?.foto_perfil_url} email={profile?.email} size="xl" editable={!soloLectura} />
            <div className="min-w-0 sm:w-full">
              <p className="truncate text-[15px] font-bold leading-snug text-foreground" title={nombre}>{nombre}</p>
              <p className="truncate text-[12px] text-muted-foreground" title={profile?.email ?? undefined}>
                {profile?.email || "Sin correo"}
              </p>
              <p className="mt-1.5 inline-flex max-w-full items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11.5px] font-medium text-primary">
                <span className="truncate">{profile?.rol_nombre || "Sin rol"}</span>
              </p>
            </div>
          </div>
        }
        pie={<p className="text-[11px] tabular-nums text-muted-foreground">Versión {APP_VERSION}</p>}
      >
        {soloLectura && (
          <p className="mb-4 flex items-start gap-2 rounded-md border border-primary bg-primary/10 px-3 py-2 text-xs text-primary-strong">
            <Eye className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            Nada se guarda mientras ves como otra persona. Para editar tu cuenta, vuelve a tu vista.
          </p>
        )}
        {seccion === "datos" && !isInmobiliariaRole && (
          <SeccionDatos
            persona={personaData ?? null}
            cargando={!!effectiveIdPersona && personaLoading}
            correo={profile?.email}
            guardando={isSavingProfile}
            creando={isCreatingPersona}
            onCrear={handleCreateAndEditProfile}
            onGuardar={handleProfileUpdate}
            editando={editandoDatos}
            onEditando={setEditandoDatos}
            soloLectura={soloLectura}
          />
        )}
        {seccion === "roles" && (
          <SeccionRoles proyectos={proyectos} vista={soloLectura ? { rolNombre: profile?.rol_nombre ?? null } : undefined} />
        )}
        {seccion === "preferencias" && <SeccionPreferencias />}
        {seccion === "seguridad" && <SeccionSeguridad />}
        {seccion === "avisos" && <SeccionAvisos open={open} />}
      </ModalSecciones>
    </>
  );
}
